import { useState, useEffect } from "react";
import {
  UserCheck,
  Mail,
  Phone,
  Shield,
  Search,
  Briefcase,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { getPlayers, getTeams, updatePlayer, togglePlayerReferee, togglePlayerDelegate } from "../lib/api";
import { toast } from "sonner";

export default function Referees() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTeam, setFilterTeam] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editFormData, setEditFormData] = useState({
    email: "",
    phone: "",
    notification_preference: "email",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [playersRes, teamsRes] = await Promise.all([
        getPlayers(),
        getTeams(),
      ]);
      setPlayers(playersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.name || "Unknown";
  };

  const handleToggleReferee = async (player) => {
    try {
      await togglePlayerReferee(player.id);
      toast.success(
        player.is_referee 
          ? `${player.name} removed as referee` 
          : `${player.name} marked as referee`
      );
      fetchData();
    } catch (error) {
      toast.error("Failed to update referee status");
    }
  };

  const handleToggleDelegate = async (player) => {
    try {
      await togglePlayerDelegate(player.id);
      toast.success(
        player.is_delegate 
          ? `${player.name} removed as delegate` 
          : `${player.name} marked as delegate`
      );
      fetchData();
    } catch (error) {
      toast.error("Failed to update delegate status");
    }
  };

  const openEditDialog = (player) => {
    setSelectedPlayer(player);
    setEditFormData({
      email: player.email || "",
      phone: player.phone || "",
      notification_preference: player.notification_preference || "email",
    });
    setEditDialogOpen(true);
  };

  const handleSaveContact = async () => {
    if (!selectedPlayer) return;
    setSaving(true);
    try {
      await updatePlayer(selectedPlayer.id, editFormData);
      toast.success("Contact info updated");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update contact info");
    } finally {
      setSaving(false);
    }
  };

  // Filter players
  const filteredPlayers = players.filter((player) => {
    const matchesSearch = player.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTeam = filterTeam === "all" || player.team_id === filterTeam;
    const matchesRole = filterRole === "all" || 
      (filterRole === "referees" && player.is_referee) ||
      (filterRole === "delegates" && player.is_delegate) ||
      (filterRole === "officials" && (player.is_referee || player.is_delegate)) ||
      (filterRole === "players" && !player.is_referee && !player.is_delegate);
    return matchesSearch && matchesTeam && matchesRole;
  });

  // Sort: officials first (referees, then delegates), then by name
  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const aScore = (a.is_referee ? 2 : 0) + (a.is_delegate ? 1 : 0);
    const bScore = (b.is_referee ? 2 : 0) + (b.is_delegate ? 1 : 0);
    if (aScore !== bScore) return bScore - aScore;
    return a.name.localeCompare(b.name);
  });

  const refereeCount = players.filter((p) => p.is_referee).length;
  const delegateCount = players.filter((p) => p.is_delegate).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="referees-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
            Officials
          </h1>
          <p className="text-muted-foreground mt-1">
            {refereeCount} Referees • {delegateCount} Delegates
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <UserCheck className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium text-primary">How it works</p>
            <p className="text-sm text-muted-foreground mt-1">
              <strong>Referees</strong> can be assigned as Center Referee or Assistant. <strong>Delegates</strong> can only be assigned as Delegate. 
              Players toggled as <strong>both</strong> can be assigned to any role. Click on an official's row to add contact info for notifications.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-background border-border"
            data-testid="search-players"
          />
        </div>
        <Select value={filterTeam} onValueChange={setFilterTeam}>
          <SelectTrigger className="w-full sm:w-48 bg-background border-border">
            <SelectValue placeholder="Filter by team" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All Teams</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-full sm:w-48 bg-background border-border">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All Players</SelectItem>
            <SelectItem value="officials">All Officials</SelectItem>
            <SelectItem value="referees">Referees Only</SelectItem>
            <SelectItem value="delegates">Delegates Only</SelectItem>
            <SelectItem value="players">Non-Officials</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Players List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left p-4 font-heading text-sm uppercase tracking-wider">
                  Player
                </th>
                <th className="text-left p-4 font-heading text-sm uppercase tracking-wider">
                  Team
                </th>
                <th className="text-left p-4 font-heading text-sm uppercase tracking-wider hidden md:table-cell">
                  Contact
                </th>
                <th className="text-center p-4 font-heading text-sm uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <UserCheck className="w-4 h-4" />
                    <span>Referee</span>
                  </div>
                </th>
                <th className="text-center p-4 font-heading text-sm uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    <span>Delegate</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((player) => {
                const isOfficial = player.is_referee || player.is_delegate;
                return (
                  <tr
                    key={player.id}
                    className={`border-b border-border/50 hover:bg-secondary/20 transition-colors ${
                      isOfficial ? "cursor-pointer" : ""
                    } ${player.is_referee ? "bg-primary/5" : ""} ${player.is_delegate ? "bg-amber-500/5" : ""}`}
                    onClick={() => isOfficial && openEditDialog(player)}
                    data-testid={`player-row-${player.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          player.is_referee ? "bg-primary/20" : 
                          player.is_delegate ? "bg-amber-500/20" : "bg-secondary"
                        }`}>
                          {player.is_referee ? (
                            <UserCheck className="w-5 h-5 text-primary" />
                          ) : player.is_delegate ? (
                            <Briefcase className="w-5 h-5 text-amber-500" />
                          ) : (
                            <span className="font-bold text-muted-foreground">
                              {player.number || "-"}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{player.name}</p>
                          {player.position && (
                            <p className="text-xs text-muted-foreground">{player.position}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{getTeamName(player.team_id)}</span>
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      {isOfficial ? (
                        <div className="flex flex-col gap-1">
                          {player.email ? (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="w-3 h-3" />
                              {player.email}
                            </span>
                          ) : (
                            <span className="text-xs text-amber-500">No email - click to add</span>
                          )}
                          {player.phone && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Phone className="w-3 h-3" />
                              {player.phone}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={player.is_referee || false}
                        onCheckedChange={() => handleToggleReferee(player)}
                        data-testid={`toggle-referee-${player.id}`}
                      />
                    </td>
                    <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={player.is_delegate || false}
                        onCheckedChange={() => handleToggleDelegate(player)}
                        className="data-[state=checked]:bg-amber-500"
                        data-testid={`toggle-delegate-${player.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {sortedPlayers.length === 0 && (
          <div className="p-12 text-center">
            <p className="text-muted-foreground">No players found matching your filters</p>
          </div>
        )}
      </div>

      {/* Edit Contact Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Edit Contact Info
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Add contact details for <strong>{selectedPlayer?.name}</strong> to receive match assignment notifications.
            </p>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, email: e.target.value })
                }
                placeholder="official@example.com"
                className="bg-background border-border mt-1"
                data-testid="official-email-input"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                value={editFormData.phone}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, phone: e.target.value })
                }
                placeholder="+1234567890"
                className="bg-background border-border mt-1"
                data-testid="official-phone-input"
              />
            </div>
            <div>
              <Label>Notification Preference</Label>
              <Select
                value={editFormData.notification_preference}
                onValueChange={(v) =>
                  setEditFormData({ ...editFormData, notification_preference: v })
                }
              >
                <SelectTrigger
                  className="bg-background border-border mt-1"
                  data-testid="notification-pref-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="email">Email Only</SelectItem>
                  <SelectItem value="sms">SMS Only (Coming Soon)</SelectItem>
                  <SelectItem value="both">Both (SMS Coming Soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveContact}
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-contact-btn"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
